import { useState } from "react";
import { Badge, Button, Card, Group, Loader, SimpleGrid, Stack, Switch, Table, Tabs, Text, TextInput, Tooltip } from "@mantine/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { passio } from "../lib/clients";
import { setClientHeaders, useClientHeaders } from "../lib/clientSettings";
import { PageHeader } from "../theme/components";
import { ErrorNotice, RequireFeature, ResultHeader } from "../components/common";
import { FoodDetailDrawer, FoodRow } from "../components/food";

function TagsTab() {
  const headers = useClientHeaders();
  const q = useQuery({ queryKey: ["ref", "tags"], queryFn: async () => (await passio.foods.tags()).data, staleTime: Infinity });
  const [filter, setFilter] = useState("");
  const toggle = (tag: string, list: "tagFilters" | "excludeTagFilters") => {
    const sc = headers.searchConfig.clone();
    sc[list] = sc[list].includes(tag) ? sc[list].filter((t) => t !== tag) : [...sc[list], tag];
    setClientHeaders(headers.with({ searchConfig: sc }));
  };
  if (q.isLoading) return <Loader color="violet" />;
  if (q.error) return <ErrorNotice error={q.error} onRetry={() => q.refetch()} />;
  const f = filter.toLowerCase();
  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap">
        <Text c="dimmed" fz="sm" maw={620}>
          Click a tag to require it in search results; shift-click to exclude it. Changes go straight into your client settings (Passio-Search-Config).
        </Text>
        <TextInput placeholder="Filter tags" value={filter} onChange={(e) => setFilter(e.currentTarget.value)} w={220} />
      </Group>
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        {(q.data?.groups ?? []).map((g) => {
          const tags = (g.tags ?? []).filter((t) => !f || t.toLowerCase().includes(f));
          if (!tags.length) return null;
          return (
            <Card key={g.key}>
              <Group justify="space-between" mb={4}>
                <Text fw={700}>{g.label ?? g.key}</Text>
                <Group gap={4}>
                  {g.multiple && <Badge size="xs" variant="outline" color="gray">multiple</Badge>}
                  {g.autoApplied && <Badge size="xs" variant="light" color="violet">auto-applied</Badge>}
                </Group>
              </Group>
              {g.description && (
                <Text fz="xs" c="dimmed" mb="xs">
                  {g.description}
                </Text>
              )}
              <Group gap={4}>
                {tags.map((t) => {
                  const inc = headers.searchConfig.tagFilters.includes(t);
                  const exc = headers.searchConfig.excludeTagFilters.includes(t);
                  return (
                    <Badge
                      key={t}
                      tt="none"
                      variant={inc || exc ? "filled" : "light"}
                      color={inc ? "lime.8" : exc ? "red" : "gray"}
                      style={{ cursor: "pointer" }}
                      onClick={(e) => toggle(t, e.shiftKey ? "excludeTagFilters" : "tagFilters")}
                    >
                      {exc ? "− " : inc ? "+ " : ""}
                      {t}
                    </Badge>
                  );
                })}
              </Group>
            </Card>
          );
        })}
      </SimpleGrid>
    </Stack>
  );
}

function ConcernsTab() {
  const headers = useClientHeaders();
  const q = useQuery({ queryKey: ["ref", "concerns"], queryFn: async () => (await passio.foods.concernCodes()).data, staleTime: Infinity });
  if (q.isLoading) return <Loader color="violet" />;
  if (q.error) return <ErrorNotice error={q.error} onRetry={() => q.refetch()} />;
  const hidden = new Set(headers.searchConfig.ignoreWithConcernFlags);
  const setHidden = (code: number, on: boolean) => {
    const sc = headers.searchConfig.clone();
    sc.ignoreWithConcernFlags = on ? [...sc.ignoreWithConcernFlags, code] : sc.ignoreWithConcernFlags.filter((c) => c !== code);
    setClientHeaders(headers.with({ searchConfig: sc }));
  };
  return (
    <Card>
      <Text c="dimmed" fz="sm" mb="sm">
        Data-quality concern flags that can appear on foods. Toggle “hide” to drop flagged foods from search (ignoreWithConcernFlags).
      </Text>
      <Table verticalSpacing="sm" highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={70}>Code</Table.Th>
            <Table.Th>Name</Table.Th>
            <Table.Th>Description</Table.Th>
            <Table.Th w={90}>Severity</Table.Th>
            <Table.Th w={80}>Hide</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {(q.data ?? []).map((c) => (
            <Table.Tr key={c.code}>
              <Table.Td ff="monospace">{c.code}</Table.Td>
              <Table.Td fw={600}>{c.name}</Table.Td>
              <Table.Td fz="sm">{c.description}</Table.Td>
              <Table.Td>
                <Badge color={(c.severity ?? 0) >= 3 ? "red" : (c.severity ?? 0) >= 2 ? "orange" : "gray"} variant="light">
                  {c.severity ?? "–"}
                </Badge>
              </Table.Td>
              <Table.Td>
                <Switch checked={c.code !== undefined && hidden.has(c.code)} onChange={(e) => c.code !== undefined && setHidden(c.code, e.currentTarget.checked)} aria-label={`Hide foods with concern ${c.code}`} />
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Card>
  );
}

function ScanDefaultsTab() {
  const [id, setId] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const m = useMutation({ mutationFn: (pid: string) => passio.foods.scanDefaults(pid) });
  return (
    <Stack gap="md">
      <Card>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (id.trim()) m.mutate(id.trim());
          }}
        >
          <Group gap="sm" align="flex-end" wrap="nowrap">
            <TextInput style={{ flex: 1 }} label="Passio ID" description="IDs from the on-device recognition model, e.g. BAK0001 or VEG0018." placeholder="VEG0018" value={id} onChange={(e) => setId(e.currentTarget.value)} />
            <Button type="submit" loading={m.isPending}>
              Get defaults
            </Button>
          </Group>
        </form>
      </Card>
      {m.error && <ErrorNotice error={m.error} />}
      {m.data && (
        <Card>
          <ResultHeader title={`Defaults for ${id}`} count={m.data.data.results?.length ?? 0} meta={m.data.meta} />
          <Stack gap={2}>
            {(m.data.data.results ?? []).map((r, i) => (
              <FoodRow key={i} item={{ displayName: r.displayName, iconId: r.iconId, refCode: r.refCode, type: r.type }} onClick={r.refCode ? () => setSelected(r.refCode!) : undefined} />
            ))}
          </Stack>
          {m.data.data.alternatives?.length ? (
            <Group gap={6} mt="sm">
              <Text fz="xs" c="dimmed">
                Alternatives:
              </Text>
              {m.data.data.alternatives.map((a, i) => (
                <Tooltip key={i} label={a.type}>
                  <Badge variant="outline" color="gray" tt="none">
                    {a.displayName}
                  </Badge>
                </Tooltip>
              ))}
            </Group>
          ) : null}
        </Card>
      )}
      <FoodDetailDrawer refCode={selected} onClose={() => setSelected(null)} />
    </Stack>
  );
}

export function ReferencePage() {
  return (
    <RequireFeature feature="reference">
      <PageHeader eyebrow="GET /napi/food/tags · /concernCodes · /scan/defaults" title="Tags & codes" description="The catalogs behind search filters, plus default foods for on-device recognition IDs." />
      <Tabs defaultValue="tags" keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="tags">Tag catalog</Tabs.Tab>
          <Tabs.Tab value="concerns">Concern codes</Tabs.Tab>
          <Tabs.Tab value="scan">Scan defaults</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="tags">
          <TagsTab />
        </Tabs.Panel>
        <Tabs.Panel value="concerns">
          <ConcernsTab />
        </Tabs.Panel>
        <Tabs.Panel value="scan">
          <ScanDefaultsTab />
        </Tabs.Panel>
      </Tabs>
    </RequireFeature>
  );
}
